import { memo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import useSWR from "swr";

import Breadcrumbs from "@mui/material/Breadcrumbs";
import Breadcrumb from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { useConnectionStore } from "stores/ConnectionStore";
import { ProvisionState, swrFetcher } from "stores/lib";

import { captureSentryException } from "utility/capture_sentry_exception";

import ServiceConnectionInstructions from "components/ServiceConnectionInstructions";

import Error from "./Error";
import CompletedPhase from "./Phase/Completed";
import ConfiguringCloudTrailPhase from "./Phase/ConfiguringCloudTrail";
import CreatingRolePhase from "./Phase/CreatingRole";
import DesignateAWSRolePhase from "./Phase/DesignateAWSRole";
import InstallCloudFormationTemplatePhase from "./Phase/InstallCloudFormationTemplatePhase";
import TestingConnectionPhase from "./Phase/TestingConnection";

// -----------------------------------------------------------------------------

enum Phase {
  InstallCloudFormationTemplate,
  DesignateAWSRole,
  ApplyNecessaryChanges,
  Completed,
  AnErrorOccurred,
}

enum ChangePhase {
  TestingConnection,
  CreatingRole,
  ConfiguringCloudTrail,
  Done,
}

const ProvisionURL = "api/organization/aws/provision";

// -----------------------------------------------------------------------------

const AWSConnectSetupPage = memo(function AWSConnectSetupPage() {
  const [, connectionActions] = useConnectionStore();

  const [phase, setPhase] = useState(Phase.InstallCloudFormationTemplate);
  const [changePhase, setChangePhase] = useState(ChangePhase.TestingConnection);
  const [refreshInterval, setRefreshInterval] = useState(2000);
  const [userId, setUserId] = useState<number>(undefined as any);
  const [errorMessage, setErrorMessage] = useState<React.ReactNode>(
    undefined as any
  );

  //
  useSWR<ProvisionStateResponse>(
    ProvisionURL,
    phase === Phase.ApplyNecessaryChanges
      ? async (url) =>
          await swrFetcher(url, {
            method: "POST",
            body: JSON.stringify({
              account_id: userId,
            }),
          })
      : null,
    {
      refreshInterval,
      onError: (error) => {
        captureSentryException(error, "Failed to provision AWS connection");
        setRefreshInterval(0);
        setPhase(Phase.AnErrorOccurred);

        //
        const { info = { Unknown: "An error occurred" } } = error;
        const message = Object.keys(info).map((key) => (
          <Typography variant="body1" key={key}>
            <strong>{key}:</strong>
            {info[key]}
          </Typography>
        ));

        //
        toast.error("There was an error connecting to Amazon Web Services", {
          position: "bottom-center",
        });
        setErrorMessage(
          (
            <Stack spacing={1} direction="column">
              {message}
            </Stack>
          ) as any
        );
      },

      onSuccess: (data) => {
        const provisionState = data?.state;
        if (provisionState !== undefined) {
          switch (provisionState) {
            case ProvisionState.QUEUED:
              setChangePhase(ChangePhase.CreatingRole);
              break;
            case ProvisionState.RUNNING:
              setRefreshInterval(1000);
              setChangePhase(ChangePhase.ConfiguringCloudTrail);
              break;

            case ProvisionState.COMPLETED:
              toast.success("Successfully connected to Amazon Web Services", {
                position: "bottom-center",
              });
              connectionActions.loadConnections({ reload: true });

              setRefreshInterval(0);
              setPhase(Phase.Completed);
              setChangePhase(ChangePhase.Done);
              break;
          }
        }
      },
    }
  );

  //
  const retryConnection = () => {
    setErrorMessage(undefined as any);
    setUserId(undefined as any);
    setChangePhase(ChangePhase.TestingConnection);
    setPhase(Phase.InstallCloudFormationTemplate);
  };

  //
  return (
    <>
      <Breadcrumbs>
        <Breadcrumb
          underline="none"
          color="inherit"
          component={Link}
          to="/environment/sources"
        >
          Environment
        </Breadcrumb>
        <Breadcrumb
          underline="none"
          color="inherit"
          component={Link}
          to="/environment/sources"
        >
          Sources
        </Breadcrumb>
        <span />
      </Breadcrumbs>
      <ServiceConnectionInstructions
        label="Amazon Web Services Connection"
        forceCollapse={
          phase > Phase.InstallCloudFormationTemplate ? new Date() : undefined
        }
      >
        <Stack spacing={2}>
          <Typography variant="body1" fontWeight="bold">
            How the ThreatKey for Amazon Web Services connection works:
          </Typography>
          <Typography variant="body1">
            Connect your ThreatKey account with Amazon Web Services in a just a
            few easy steps. By connecting Amazon Web Services to ThreatKey, you
            can get a comprehensive view of your AWS environment&apos;s security
            posture, identify and prioritize risks, and remediate them quickly
            and easily.
          </Typography>
        </Stack>
      </ServiceConnectionInstructions>

      <Stack spacing={2}>
        <InstallCloudFormationTemplatePhase
          disabled={phase !== Phase.InstallCloudFormationTemplate}
          onNext={() => setPhase(Phase.DesignateAWSRole)}
        />

        <DesignateAWSRolePhase
          disabled={phase !== Phase.DesignateAWSRole}
          onNext={(newUserId: number) => {
            setUserId(newUserId);
            setPhase(Phase.ApplyNecessaryChanges);
          }}
          onBack={() => setPhase(Phase.InstallCloudFormationTemplate)}
        />

        {phase >= Phase.ApplyNecessaryChanges ? (
          <>
            <Error
              show={
                changePhase === ChangePhase.TestingConnection &&
                phase === Phase.AnErrorOccurred
              }
              onTryAgain={retryConnection}
            >
              {errorMessage}
            </Error>

            <TestingConnectionPhase
              disabled={changePhase !== ChangePhase.TestingConnection}
              userId={userId}
              progressIcon={
                changePhase === ChangePhase.TestingConnection &&
                phase !== Phase.AnErrorOccurred
              }
              successIcon={changePhase > ChangePhase.TestingConnection}
              errorIcon={
                changePhase === ChangePhase.TestingConnection &&
                phase === Phase.AnErrorOccurred
              }
            />

            <Error
              show={
                changePhase === ChangePhase.CreatingRole &&
                phase === Phase.AnErrorOccurred
              }
              onTryAgain={retryConnection}
            >
              {errorMessage}
            </Error>

            <CreatingRolePhase
              disabled={changePhase !== ChangePhase.CreatingRole}
              progressIcon={
                changePhase === ChangePhase.CreatingRole &&
                phase !== Phase.AnErrorOccurred
              }
              successIcon={
                changePhase > ChangePhase.CreatingRole &&
                phase !== Phase.AnErrorOccurred
              }
              errorIcon={
                changePhase === ChangePhase.CreatingRole &&
                phase === Phase.AnErrorOccurred
              }
            />

            <Error
              show={
                changePhase === ChangePhase.ConfiguringCloudTrail &&
                phase === Phase.AnErrorOccurred
              }
              onTryAgain={retryConnection}
            >
              {errorMessage}
            </Error>

            <ConfiguringCloudTrailPhase
              disabled={changePhase !== ChangePhase.ConfiguringCloudTrail}
              progressIcon={
                changePhase === ChangePhase.ConfiguringCloudTrail &&
                phase !== Phase.AnErrorOccurred
              }
              successIcon={phase === Phase.Completed}
              errorIcon={
                changePhase === ChangePhase.ConfiguringCloudTrail &&
                phase === Phase.AnErrorOccurred
              }
            />

            <CompletedPhase
              disabled={phase !== Phase.Completed}
              successIcon={phase === Phase.Completed}
              onClickFn={() => {
                connectionActions.loadConnections({ reload: true });
              }}
            />
          </>
        ) : null}
      </Stack>
    </>
  );
});

export default AWSConnectSetupPage;

// -----------------------------------------------------------------------------

interface ProvisionStateResponse {
  state: ProvisionState;
}
